// eslint-disable-next-line
import React, { FC, useState } from 'react';

import * as Yup from 'yup';

import { CustomTextField, FormSubTitle, InputWrapper } from 'components/forms';
import { Field, FieldArray, FieldArrayRenderProps, useField } from 'formik';
import {
  PhoneFields,
  PhoneTypes,
  validateAreaCode,
  validateFullPhoneLength,
} from 'core/phones';
import {
  REGEX_AREACODE,
  REGEX_SINGLE_SPACES_BEETWEN_WORDS,
  RGX_NO_SPECIAL_CHARACTERS,
  regexSequentialPhone,
  withNamespace,
} from 'utils/helpers';

import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import IconButton from '@material-ui/core/IconButton';
import Modal from 'components/Modal';
import OpacityAnimation from 'components/OpacityAnimation';
import SlideAnimation from 'components/SlideAnimation';
import { AnimatePresence } from 'framer-motion';
import { VARIANTS } from 'utils/motion-variants';
import styled from 'styled-components';
import { spacing } from '@material-ui/system';
import { Box } from '@material-ui/core';
import dictionary from 'utils/dictionary';

const GridBox = styled(Grid)`
  ${spacing}
`;

/** * SETUP ** */
// fields

export const fields = {
  NAME: 'name',
  SURNAME: 'surname',
  PHONE_TYPE: 'phoneType',
  AREACODE: 'areaCode',
  PHONE_NUMBER: 'phoneNumber',
  PHONE_COMPANY: 'phoneCompany',
  EMAIL: 'email',
};

export const initialContact = {
  [fields.NAME]: '',
  [fields.SURNAME]: '',
  [fields.PHONE_TYPE]: '',
  [fields.AREACODE]: '',
  [fields.PHONE_NUMBER]: '',
  [fields.PHONE_COMPANY]: '',
  [fields.EMAIL]: '',
};

const initialValues = [];
type InitialValues = typeof initialContact[];

// validation schema
const validationSchema = () =>
  Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .required()
        .min(2)
        .max(30)
        .matches(
          REGEX_SINGLE_SPACES_BEETWEN_WORDS.regex,
          REGEX_SINGLE_SPACES_BEETWEN_WORDS.errorMessage
        ),
      surname: Yup.string()
        .required()
        .min(2)
        .max(30)
        .matches(
          REGEX_SINGLE_SPACES_BEETWEN_WORDS.regex,
          REGEX_SINGLE_SPACES_BEETWEN_WORDS.errorMessage
        ),
      email: Yup.string().email().max(45).required(),
      phoneType: Yup.string().required(),
      phoneCompany: Yup.string().when('phoneType', (phoneType, schema) => {
        if (phoneType === PhoneTypes.Celular) {
          return schema.required();
        }
        return schema;
      }),
      areaCode: Yup.string()
        .required()
        .min(2)
        .max(4)
        .matches(REGEX_AREACODE.regex, REGEX_AREACODE.options)
        .test(
          'test-areacode',
          'El código de área es inexistente.',
          validateAreaCode
        ),
      phoneNumber: Yup.string()
        .required()
        .test(
          '6 Sequential Numbers',
          'Formato de teléfono no válido',
          (value) => {
            return !regexSequentialPhone.test(value);
          }
        )
        .when('areaCode', validateFullPhoneLength),
    })
  );

type Props = { namespace: string };

const ContactListSubForm: FC<Props> = ({ namespace }) => {
  const [{ value }] = useField(namespace);

  const [contacts, setContacts] = useState(value);

  const [isOpenRemoveContactModal, setOpenRemoveContactModal] = useState(false);
  const [extraContactToRemoveIndex, setExtraContactToRemoveIndex] = useState(0);

  const handleAddContactClick = (arrayHelpers: FieldArrayRenderProps) => {
    const newContact = { ...initialContact, isDynamic: true };
    arrayHelpers.push(newContact);
    setContacts((prev) => [...prev, newContact]);
  };

  const handleContactRemoveClick = (index: number) => {
    setOpenRemoveContactModal(true);
    setExtraContactToRemoveIndex(index);
  };

  return (
    <FieldArray name={namespace}>
      {(arrayHelpers) => {
        return (
          <>
            {contacts.length > 0 && (
              <OpacityAnimation>
                <FormSubTitle>
                  {dictionary.clientForm_adictionalContact_subtitle}
                </FormSubTitle>
              </OpacityAnimation>
            )}

            <AnimatePresence
              onExitComplete={() => {
                arrayHelpers.remove(extraContactToRemoveIndex);
              }}
            >
              {contacts.map((contact, index) => {
                return (
                  <SlideAnimation
                    key={index}
                    variants={VARIANTS.LIST}
                    direction="down"
                    exit="exit"
                    initial="close"
                    animate="open"
                    animatePresence={false}
                  >
                    <GridBox mb={4} data-testid="contact">
                      <Grid container>
                        <InputWrapper>
                          <Field
                            id={withNamespace(
                              `${namespace}[${index}]`,
                              fields.NAME
                            )}
                            name={withNamespace(
                              `${namespace}[${index}]`,
                              fields.NAME
                            )}
                            label={dictionary.clientForm_label_name}
                            regex={RGX_NO_SPECIAL_CHARACTERS}
                            component={CustomTextField}
                            showErrorOnMount={!contact?.isDynamic}
                          />
                        </InputWrapper>

                        <InputWrapper>
                          <Field
                            id={withNamespace(
                              `${namespace}[${index}]`,
                              fields.SURNAME
                            )}
                            name={withNamespace(
                              `${namespace}[${index}]`,
                              fields.SURNAME
                            )}
                            label={dictionary.clientForm_label_surname}
                            regex={RGX_NO_SPECIAL_CHARACTERS}
                            component={CustomTextField}
                            showErrorOnMount={!contact?.isDynamic}
                          />
                        </InputWrapper>

                        <PhoneFields
                          namespace={`${namespace}[${index}]`}
                          showErrorOnMount={!contact?.isDynamic}
                        />

                        <InputWrapper>
                          <Field
                            id={withNamespace(
                              `${namespace}[${index}]`,
                              fields.EMAIL
                            )}
                            name={withNamespace(
                              `${namespace}[${index}]`,
                              fields.EMAIL
                            )}
                            label={dictionary.clientForm_label_email}
                            component={CustomTextField}
                            showErrorOnMount={!contact?.isDynamic}
                          />
                        </InputWrapper>
                        <Box mt={1} alignSelf="baseline">
                          <IconButton
                            color="primary"
                            role="remove-btn"
                            onClick={() => {
                              handleContactRemoveClick(index);
                            }}
                          >
                            <HighlightOffIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    </GridBox>
                  </SlideAnimation>
                );
              })}
            </AnimatePresence>
            <Box alignSelf="flex-start">
              <Button
                size="medium"
                color="primary"
                disabled={contacts.length > 1}
                startIcon={<AddIcon />}
                onClick={() => handleAddContactClick(arrayHelpers)}
              >
                {dictionary.clientForm_adictionalContact_addButton}
              </Button>
            </Box>

            <Modal
              maxWidth="sm"
              open={isOpenRemoveContactModal}
              title={dictionary.clientForm_delete_contact_modal_title}
              onOk={() => {
                setOpenRemoveContactModal(false);
                setContacts((prev) =>
                  prev.filter((pc, i) => extraContactToRemoveIndex !== i)
                );
              }}
              onCancel={() => setOpenRemoveContactModal(false)}
            />
          </>
        );
      }}
    </FieldArray>
  );
};

export type ContactListInitialValues = InitialValues;
export const contactListValidationSchema = validationSchema;
export const contactListInitialValues = initialValues;

export default ContactListSubForm;
